import React, { useState } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { SimilarityBar } from './SimilarityBar';
import { BarcodeStatusDot } from './BarcodeStatusDot';
import { ForgeCandidateDesign } from '../../types';

const MAX_NUMBER_KEYS = 7;

export interface DesignPickerPanelProps {
  title: string;
  candidates: ForgeCandidateDesign[];
  /** Index of the keyboard-highlighted candidate */
  selectedIdx: number;
  onSelectIdx: (idx: number) => void;
  /** Called when user clicks a candidate (or presses number key) */
  onPick: (design: ForgeCandidateDesign) => void;
  /** Optional ID of an already-chosen design (shows accent border) */
  chosenDesignId?: string;
  /** Optional footer content (e.g. "Unknown Product" button) */
  footer?: React.ReactNode;
}

export const DesignPickerPanel: React.FC<DesignPickerPanelProps> = ({
  title,
  candidates,
  selectedIdx,
  onSelectIdx,
  onPick,
  chosenDesignId,
  footer,
}) => {
  const [expandedDesigns, setExpandedDesigns] = useState<Set<string>>(new Set());

  const toggleExpanded = (designId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedDesigns((prev) => {
      const next = new Set(prev);
      if (next.has(designId)) next.delete(designId);
      else next.add(designId);
      return next;
    });
  };

  return (
    <div
      style={{
        width: 360,
        minWidth: 360,
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius)',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: '10px 14px',
          borderBottom: '1px solid var(--border)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-primary)' }}>
          {title}
        </span>
        <span style={{ fontSize: 10, color: 'var(--text-tertiary)' }}>
          <span className="kbd" style={{ fontSize: 9 }}>
            1-{Math.min(candidates.length, MAX_NUMBER_KEYS)}
          </span>{' '}
          select &middot;{' '}
          <span className="kbd" style={{ fontSize: 9 }}>Esc</span>{' '}
          cancel
        </span>
      </div>

      {/* Candidate list */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '4px 0' }}>
        {candidates.map((design, idx) => {
          const isSelected = idx === selectedIdx;
          const isChosen = chosenDesignId === design.id;
          const numberKey = idx < MAX_NUMBER_KEYS ? idx + 1 : null;
          const isExpanded = expandedDesigns.has(design.id);
          return (
            <div key={design.id}>
              <div
                onClick={() => onPick(design)}
                onMouseEnter={() => onSelectIdx(idx)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '8px 14px',
                  cursor: 'pointer',
                  background: isSelected
                    ? 'var(--elevated)'
                    : isChosen
                      ? 'rgba(59,130,246,0.08)'
                      : 'transparent',
                  borderLeft: isSelected
                    ? '2px solid var(--accent)'
                    : '2px solid transparent',
                  transition: 'background 80ms ease',
                }}
              >
                {numberKey && (
                  <span
                    className="kbd"
                    style={{
                      fontSize: 10,
                      minWidth: 18,
                      textAlign: 'center',
                      padding: '1px 4px',
                      flexShrink: 0,
                    }}
                  >
                    {numberKey}
                  </span>
                )}
                <img
                  src={design.representativeImageUrl}
                  alt={design.id}
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: 4,
                    objectFit: 'cover',
                    border: isChosen
                      ? '2px solid var(--accent)'
                      : '1px solid var(--border)',
                  }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: 'var(--text-primary)',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {design.id}
                  </div>
                  <div
                    style={{
                      fontSize: 10,
                      color: 'var(--text-tertiary)',
                      fontFamily: 'var(--font-mono)',
                    }}
                  >
                    {design.upc} &middot; {design.cropCount} crops
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <SimilarityBar value={design.similarity} />
                  <BarcodeStatusDot status={design.barcodeStatus} />
                </div>
                {design.sampleCrops.length > 0 && (
                  <div
                    onClick={(e) => toggleExpanded(design.id, e)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      cursor: 'pointer',
                      color: 'var(--text-tertiary)',
                      flexShrink: 0,
                    }}
                  >
                    {isExpanded ? (
                      <ExpandLessIcon style={{ fontSize: 16 }} />
                    ) : (
                      <ExpandMoreIcon style={{ fontSize: 16 }} />
                    )}
                  </div>
                )}
              </div>
              {/* Expanded sample crops */}
              {isExpanded && design.sampleCrops.length > 0 && (
                <div
                  style={{
                    display: 'flex',
                    gap: 4,
                    padding: '4px 14px 8px 56px',
                    flexWrap: 'wrap',
                  }}
                >
                  {design.sampleCrops.map((sample) => (
                    <img
                      key={sample.id}
                      src={sample.imageUrl}
                      alt={sample.id}
                      style={{
                        width: 40,
                        height: 40,
                        borderRadius: 3,
                        objectFit: 'cover',
                        border: '1px solid var(--border)',
                      }}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Optional footer */}
      {footer && (
        <div style={{ padding: '10px 14px', borderTop: '1px solid var(--border)' }}>
          {footer}
        </div>
      )}
    </div>
  );
};
